"use client";

import Image from "next/image";
import NavLink from "next/link";
import {
  Badge,
  Button,
  Dropdown,
  Link,
  SpheronApp,
  Navbar as SpheronNav,
  Tooltip,
} from "@spheron/ui-library";
import Wallet from "@spheron/ui-library/dist/assets/wallet.svg";
import Console from "@spheron/ui-library/dist/assets/compute/console.svg";
import ProviderEarning from "@spheron/ui-library/dist/assets/compute/provider-earning.svg";

import DashboardIcon from "@spheron/ui-library/dist/assets/spheron-specific/dashboard.svg";
import LeaderboardIcon from "@/assets/leaderboard.svg";
import MyFizzIcon from "@spheron/ui-library/dist/assets/spheron-specific/multi-services.svg";
import GatewayIcon from "@spheron/ui-library/dist/assets/spheron-specific/gateway.svg";
import SetupIcon from "@spheron/ui-library/dist/assets/setting.svg";
import DocsIcon from "@spheron/ui-library/dist/assets/document.svg";
import AddIcon from "@spheron/ui-library/dist/assets/spheron-specific/outline-add.svg";

import BlogsIcon from "@spheron/ui-library/dist/assets/document-2.svg";
import PenIcon from "@spheron/ui-library/dist/assets/pen.svg";
import Disconnect from "@spheron/ui-library/dist/assets/logout.svg";
import LogoutIcon from "@spheron/ui-library/dist/assets/power-on.svg";
import SpheronMenu from "@spheron/ui-library/dist/assets/spheron-menu.svg";
import SpheronLogo from "@spheron/ui-library/dist/assets/spheron-specific/spheron-logo.svg";
import SpheronIcon from "@spheron/ui-library/dist/assets/spheron-specific/spheron-logo.svg";
import React, { useEffect, useRef, useState } from "react";
import { useParams, usePathname, useRouter } from "next/navigation";
import makeBlockie from "ethereum-blockies-base64";
import { getCookie, isMobileDevice, truncateAddress } from "@/utils";
import { addUserData } from "@/services/user";
import { trackUserEvent } from "@/utils/analytics";
import { getFizzNodeByAddress } from "@/services/fizz";
import { DISABLE_CREATE_AND_UPDATE_NODE, thirdwebClient } from "@/config";
import {
  useActiveAccount,
  useConnectModal,
  useDisconnect,
  useAutoConnect,
  useActiveWallet,
  useWalletDetailsModal,
} from "thirdweb/react";
import { wallets } from "@/config/thirdweb-config";

const Navbar = () => {
  const hasBeenSaved = useRef(false);
  const pathname = usePathname();
  const params = useParams();
  const router = useRouter();
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const { connect } = useConnectModal();
  const { disconnect } = useDisconnect();
  const detailsModal = useWalletDetailsModal();

  useAutoConnect({
    client: thirdwebClient,
    wallets,
  });

  const [hasFizzRegistered, setHasFizzRegistered] = useState(false);
  const [isRegisterFizzDisabled, setIsRegisterFizzDisabled] = useState(false);
  const [registerButtonLoading, setRegisterButtonLoading] = useState(false);

  const dropdownItems = [
    {
      id: 1,
      label: "CONSOLE",
      handleClick: () => {
        trackUserEvent("Navbar Menu Console App Click");
        window.open("https://console.spheron.network");
      },
      icon: <Console className="w-8 h-8" />,
    },
    {
      id: 2,
      label: "PROVIDER",
      handleClick: () => {
        trackUserEvent("Navbar Menu Provider App Click");
        window.open("https://provider.spheron.network");
      },
      icon: <ProviderEarning className="w-8 h-8" />,
    },
    // {
    //   id: 3,
    //   label: (
    //     <div className="flex flex-row gap-x-2 items-center">
    //       <span>CLOUD</span>
    //       <span>
    //         <Badge
    //           text="Unavailable"
    //           size="small"
    //           solid={false}
    //           badgeType="default"
    //         />
    //       </span>
    //     </div>
    //   ),
    //   handleClick: () => {
    //     trackUserEvent("Navbar Menu Cloud App Click");
    //     window.open("https://app.spheron.network");
    //   },
    //   icon: <CloudApp className="w-8 h-8" />,
    //   disabled: true,
    // },
  ];

  useEffect(() => {
    if (account && params.fizzAddress) {
      if (
        account?.address?.toLowerCase() !==
          (params.fizzAddress as string)?.toLowerCase() &&
        (pathname.includes("register") || pathname.includes("my-fizz")) /*  ||
          pathname.includes("installation") */
      ) {
        router.push("/");
      }
    }
  }, [account, params.fizzAddress, pathname, router]);

  useEffect(() => {
    const email = getCookie("email");
    if (email && account && !hasBeenSaved.current) {
      addUserData(email, account?.address);
      hasBeenSaved.current = true;
    }
  }, [account]);

  useEffect(() => {
    if (wallet) {
      wallet?.subscribe("accountChanged", (account) => {
        if (account) {
          localStorage?.setItem("fizzNodeAddress", account?.address || "");
          router.push("/");
        }
      });
    }
  }, [router, wallet]);

  useEffect(() => {
    if (account) {
      setIsRegisterFizzDisabled(true);
      getFizzNodeByAddress(account?.address)
        .then((response) => {
          if (
            response.joinTimestamp.toString() !== "0" &&
            response.fizzId.toString() !== "0"
          ) {
            setHasFizzRegistered(true);
          } else {
            setHasFizzRegistered(false);
          }
          setIsRegisterFizzDisabled(false);
        })
        .catch(() => {
          setHasFizzRegistered(false);
        })
        .finally(() => {
          setIsRegisterFizzDisabled(false);
        });
    } else {
      setHasFizzRegistered(false);
    }
  }, [account]);

  const handleRegisterClick = async () => {
    if (DISABLE_CREATE_AND_UPDATE_NODE) {
      return;
    }

    let accountAddress;

    if (!account) {
      const wallet = await connect({
        client: thirdwebClient,
        wallets,
        size: "compact",
      });

      accountAddress = wallet?.getAccount()?.address;
    } else {
      accountAddress = account?.address;
    }

    if (accountAddress) {
      localStorage?.setItem("fizzNodeAddress", accountAddress || "");
      setRegisterButtonLoading(true);
      getFizzNodeByAddress(accountAddress || "")
        .then((response) => {
          if (
            response.joinTimestamp.toString() !== "0" &&
            response.fizzId.toString() !== "0"
          ) {
            trackUserEvent("View My Fizz", {
              address: accountAddress,
            });
            router.push(`/${accountAddress}/dashboard`);
          } else {
            trackUserEvent("Register New Fizz Node", {
              address: accountAddress,
            });
            router.push(`/${accountAddress}/register`);
          }
        })
        .catch((err) => {
          router.push(`/${accountAddress}/register`);
          if (err?.revert?.name === "FizzNodeNotFound") {
            trackUserEvent("Register New Fizz Node", {
              address: accountAddress,
            });
          }
        });
      setRegisterButtonLoading(false);
    }
  };

  const isMobile =
    typeof window !== "undefined" ? isMobileDevice(navigator.userAgent) : false;

  return (
    <SpheronNav
      logoIcon={
        <div className="flex items-center justify-start gap-x-2 md:ml-0 -ml-6">
          <Dropdown
            dropdownType="button"
            className="w-fit"
            dropdownSize="default"
            bordersNone
            dropdownClassname="w-fit !ml-1"
            buttonImage={
              <button
                className="p-0.5 rounded
                transition hover:bg-action-ghost-press dark:hover:bg-dark-action-ghost-press"
                onClick={() => trackUserEvent("Navbar Menu Click")}
              >
                <SpheronMenu className="w-8 h-8 text-logo-primary dark:text-dark-logo-primary" />
              </button>
            }
            options={dropdownItems.map((app) => {
              return {
                id: app.id,
                optionType: "primary",
                dropdownRowClassname:
                  "hover:bg-transparent dark:hover:bg-transparent",
                label: (
                  <div className="w-80">
                    <SpheronApp
                      heading={
                        <div className="flex items-center justify-start gap-x-2">
                          <SpheronIcon className="w-6 h-6 text-logo-primary dark:text-dark-logo-primary" />
                          <span className="font-semibold">{app.label}</span>
                        </div>
                      }
                      helpText=""
                      icon={app.icon}
                    />
                  </div>
                ),
                handleClick: () => app.handleClick(),
                // disable: app.disabled,
              };
            })}
          />
          <NavLink
            onClick={() => {
              trackUserEvent("Navbar Logo Click");
            }}
            href={`/`}
          >
            <div className="flex items-center justify-start gap-x-1">
              <SpheronLogo className="w-10 h-10 text-logo-primary dark:text-dark-logo-primary" />
              <span className="text-base font-semibold md:block hidden">
                FIZZ NODE
              </span>
            </div>
          </NavLink>
        </div>
      }
      navButtons={
        <div className="flex items-center justify-start gap-x-4">
          <Button
            label="Dashboard"
            size="small"
            buttonType="action"
            classname={
              pathname === `/`
                ? // ||(pathname || "").includes("new-deployment")
                  "!bg-action-ghost-press dark:!bg-dark-action-ghost-hover"
                : ""
            }
            leftIcon={<DashboardIcon className="w-5 h-5" />}
            onClick={() => {
              trackUserEvent("Navbar Dashboard Button Click");
              router.push("/");
            }}
          />

          <Button
            label="Leaderboard"
            size="small"
            buttonType="action"
            classname={
              (pathname || "") === `/leaderboard`
                ? "!bg-action-ghost-press dark:!bg-dark-action-ghost-hover"
                : ""
            }
            leftIcon={<LeaderboardIcon className="w-5 h-5" />}
            onClick={() => {
              trackUserEvent("Navbar Leaderboard Button Click");
              router.push(`/leaderboard`);
            }}
          />

          <Button
            label="Gateways"
            size="small"
            buttonType="action"
            classname={
              (pathname || "").includes("/gateways")
                ? "!bg-action-ghost-press dark:!bg-dark-action-ghost-hover"
                : ""
            }
            leftIcon={<GatewayIcon className="w-5 h-5" />}
            onClick={() => {
              trackUserEvent("Navbar Gateways Click");
              router.push(`/gateways`);
            }}
          />

          {account && hasFizzRegistered && (
            <>
              <Button
                classname={`${
                  (pathname || "").includes(`/${account?.address}/dashboard`)
                    ? "!bg-action-ghost-press dark:!bg-dark-action-ghost-hover"
                    : ""
                } whitespace-nowrap`}
                buttonType="action"
                onClick={() => {
                  router.push(`/${account?.address}/dashboard`);
                }}
                label="My Fizz"
                leftIcon={<MyFizzIcon className="w-5 h-5" />}
                size="small"
              />
              <Button
                classname={
                  (pathname || "").includes("/installation")
                    ? "!bg-action-ghost-press dark:!bg-dark-action-ghost-hover"
                    : ""
                }
                buttonType="action"
                onClick={() => {
                  router.push(`/${account?.address}/installation`);
                }}
                label="Setup"
                leftIcon={<SetupIcon className="w-5 h-5" />}
                size="small"
                // disabled={isRegisterFizzDisabled}
                // loading={registerButtonLoading}
              />
            </>
          )}

          {/* <Button
            label="Documentation"
            size="small"
            buttonType="action"
            classname={
              (pathname || "").includes("/documentation")
                ? "!bg-action-ghost-press dark:!bg-dark-action-ghost-hover"
                : ""
            }
            leftIcon={<DocsIcon className="w-5 h-5" />}
            onClick={() => {
              trackUserEvent("Navbar Docs Click");
              window.open(`https://docs.spheron.network/fizz`);
            }}
          /> */}
        </div>
      }
      infoElements={
        <>
          <div className="flex items-center justify-start gap-x-4 md:mr-0 -mr-6">
            <Link
              classname="md:block hidden"
              type="secondary"
              size="default"
              // text="Documentation"
              text="Docs"
              isBold={false}
              onClick={() => {
                trackUserEvent("Navbar Docs Click");
                window.open(`https://docs.spheron.network/fizz`);
              }}
            />
            {/* <Link
              type="secondary"
              size="default"
              text="Community"
              isBold={false}
              onClick={() => router.push(`https://community.spheron.network/`)}
            /> */}

            <>
              {!hasFizzRegistered && (
                <Button
                  classname="ml-auto w-max justify-center px-4 gap-1.5 whitespace-nowrap"
                  buttonType="primary"
                  onClick={handleRegisterClick}
                  label="Register Fizz Node"
                  rightIcon={<AddIcon />}
                  size="small"
                  disabled={
                    isRegisterFizzDisabled ||
                    isMobile ||
                    DISABLE_CREATE_AND_UPDATE_NODE
                  }
                  loading={registerButtonLoading}
                />
              )}
              {account ? (
                <Dropdown
                  dropdownClassname="w-[200px] items-center bg-base-bg"
                  dropdownType="button"
                  options={[
                    // {
                    //   optionType: "primary",
                    //   id: 0,
                    //   label: (
                    //     <div className="flex flex-row items-center justify-center gap-x-2">
                    //       <UsersIcon className="w-6 h-6" />
                    //       <span>Community</span>
                    //     </div>
                    //   ),
                    //   value: "Community",
                    //   handleClick: () => {
                    //     window.open("https://community.spheron.network/");
                    //   },
                    // },
                    {
                      optionType: "primary",
                      id: 1,
                      label: (
                        <div className="flex flex-row items-center justify-center gap-x-2">
                          <Wallet className="w-6 h-6" />
                          <span>Manage Wallet</span>
                        </div>
                      ),
                      value: "Manage",
                      handleClick: () => {
                        detailsModal.open({
                          client: thirdwebClient,
                          theme: "dark",
                        });
                      },
                    },
                    {
                      optionType: "primary",
                      id: 1,
                      label: (
                        <div className="flex flex-row items-center justify-center gap-x-2">
                          <BlogsIcon className="w-6 h-6" />
                          <span>Blogs</span>
                        </div>
                      ),
                      value: "Blogs",
                      handleClick: () => {
                        window.open("https://blog.spheron.network/");
                      },
                    },
                    // {
                    //   optionType: "primary",
                    //   id: 2,
                    //   label: (
                    //     <div className="flex flex-row items-center justify-center gap-x-2">
                    //       <PenIcon className="w-6 h-6" />
                    //       <span>Report Bugs</span>
                    //     </div>
                    //   ),
                    //   value: "Report Bugs",
                    //   handleClick: () => {
                    //     window.open("https://blog.spheron.network/");
                    //   },
                    // },
                    ...(isMobile
                      ? [
                          {
                            optionType: "primary" as const,
                            id: 5,
                            label: (
                              <div className="flex flex-row md:hidden items-center justify-center gap-x-2">
                                <DocsIcon className="w-6 h-6" />
                                <span>Docs</span>
                              </div>
                            ),
                            value: "Docs",
                            handleClick: () => {
                              window.open("https://docs.spheron.network/fizz");
                            },
                          },
                        ]
                      : []),
                    {
                      optionType: "primary",
                      id: 3,
                      label: (
                        <div className="flex flex-row items-center justify-center gap-x-2">
                          <Disconnect className="text-action-error-default w-6 h-6" />
                          <span className="text-action-error-default">
                            Disconnect Wallet
                          </span>
                        </div>
                      ),
                      value: "Disconnect",
                      handleClick: () => {
                        if (wallet) {
                          disconnect(wallet);
                        }
                        trackUserEvent("Disconnect Wallet");
                        router.push("/");
                      },
                    },
                    {
                      optionType: "primary",
                      id: 4,
                      label: (
                        <div className="flex flex-row items-center justify-center gap-x-2">
                          <LogoutIcon className="text-action-error-default w-6 h-6" />
                          <span className="text-action-error-default">
                            Logout
                          </span>
                        </div>
                      ),
                      value: "Logout",
                      handleClick: () => {
                        trackUserEvent("Logout");
                        router.push(
                          `https://app.spheron.network/logout?redirectUrl=${encodeURIComponent(
                            "https://fizz.spheron.network"
                          )}`
                        );
                      },
                    },
                  ]}
                  buttonImage={
                    <div
                      title={account?.address || ""}
                      className="flex flex-row gap-x-2 items-center"
                    >
                      <Image
                        width={36}
                        height={36}
                        src={makeBlockie(account?.address || "")}
                        alt=""
                        priority
                        className="rounded-full"
                      />
                      <span className="text-base-heading-text-color dark:text-dark-base-heading-text-color">
                        {truncateAddress(account?.address || "")}
                      </span>
                    </div>
                  }
                  dropdownSize="compact"
                  bordersNone
                />
              ) : (
                <>
                  <Tooltip
                    position="left"
                    text="Connect your crypto wallet to track rewards and manage your node"
                    type="float"
                    wrapText
                  >
                    <Button
                      classname="w-max whitespace-nowrap"
                      label="Connect Wallet"
                      size="small"
                      buttonType="primary"
                      leftIcon={<Wallet className="w-5 h-5" />}
                      onClick={async () => {
                        const wallet = await connect({
                          client: thirdwebClient,
                          wallets,
                          size: "compact",
                        });
                        if (wallet) {
                          trackUserEvent("Connect Wallet");
                        }
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </>
          </div>
        </>
      }
    />
  );
};

export default Navbar;
